import _ from 'underscore';

import Decimal from 'app/src/data/base/Decimal';
import Localize from 'app/src/localize/Localize';

const Format = {
  /**
   * Parses a string into an integer.  If the value specified is not a number then either the given default
   * value or the original value will be returned.
   *
   * @param integerAsString {string}    String value to be converted into an integer.
   * @param defaultValue {number}      optional default integer value to return if given
   *                                   value fails to convert to integer
   * @returns {Number|*}      Returns the value as an integer if the value can be converted.  If
   *                          the value cannot be converted then defaultValue is returned (if defined) otherwise
   *                          the original value is returned.
   */
  parseInt(integerAsString, defaultValue) {
    var parsedValue = parseInt(integerAsString);
    if (_.isNaN(parsedValue)) {
      if (!_.isUndefined(defaultValue)) {
        parsedValue = defaultValue;
      } else {
        parsedValue = integerAsString;
      }
    }
    return parsedValue;
  },

  /**
   * Parses a string into an float.  If the value specified is not a number then either the given default
   * value or the original value will be returned.
   *
   * @param floatAsString {string}    String value to be converted into a float.
   * @param defaultValue {number}     optional default numeric value to return if given value
   *                                  fails to convert to a float
   * @returns {BigNumber|*}   Returns the value as a Decimal number if the value can be converted.  If
   *                          the value cannot be converted then defaultValue is returned (if defined) otherwise
   *                          the original value is returned.
   */
  parseFloat(floatAsString, defaultValue) {
    var parsedValue = parseFloat(floatAsString);
    if (_.isNaN(parsedValue)) {
      if (!_.isUndefined(defaultValue)) {
        parsedValue = defaultValue;
      } else {
        parsedValue = floatAsString;
      }
    }
    return parsedValue;
  },

  /**
   * Parses a string into a float.  If the value specified cannot be converted then either the given default
   * value or the original value will be returned.  Exponential expressions are not allowed.
   *
   * @param  {string} decimalAsString   String value to be converted into a float.
   * @param  {Decimal|number} [defaultValue]    optional default Decimal value to return if the given value fails to convert
   * @returns {any}       Returns the value as a Decimal number if the value can be converted.  If
   *                          the value cannot be converted then defaultValue is returned (if defined) otherwise
   *                          the original value is returned.
   */
  parseDecimal(decimalAsString, defaultValue) {
    const exponentialMatcher = /[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)/;
    let number;

    try {
      number = new Decimal(decimalAsString);

      if (
        number.isNaN() ||
        (_.isString(decimalAsString) && decimalAsString.match(exponentialMatcher))
      ) {
        if (!_.isUndefined(defaultValue)) {
          number = new Decimal(defaultValue);
        } else {
          number = decimalAsString;
        }
      }
    } catch (error) {
      if (!_.isUndefined(defaultValue)) {
        number = new Decimal(defaultValue);
      } else {
        number = decimalAsString;
      }
    }

    return number;
  },

  /**
   * Parses a string into a boolean.  If the value specified is not a string then either the given default
   * value or the original value will be returned.
   *
   * @param booleanAsString {string}    String value ('true', 'false', '1', '0') to be converted
   *                                    into a boolean.  The value
   *                          is case insensitive.  When the value is '1' or '0' it evaluates to true and false
   *                          respectively.
   * @param defaultValue {boolean}     optional boolean value to  return if given value does not convert to a boolean
   * @returns {Boolean|*}     Returns the value as a boolean if the value can be converted.  If
   *                          the value cannot be converted then defaultValue is returned (if defined) otherwise
   *                          the original value is returned.
   */
  parseBoolean(booleanAsString, defaultValue) {
    let parsedValue = booleanAsString;
    if (!_.isBoolean(booleanAsString)) {
      if (_.isString(booleanAsString)) {
        if (booleanAsString.toLowerCase() === 'true' || booleanAsString === '1') {
          parsedValue = true;
        } else if (booleanAsString.toLowerCase() === 'false' || booleanAsString === '0') {
          parsedValue = false;
        }
      }
      // If still not converted properly return defaultValue if given
      if (!_.isBoolean(parsedValue) && _.isBoolean(defaultValue)) {
        parsedValue = defaultValue;
      }
    }
    return parsedValue;
  },

  /**
   * Formats a number into a monetary string representation that includes a localized currency symbol
   * and precision. e.g. 12345.67890 -> "$12,345.68"
   *
   * @param  {string|number|Decimal}  amount   The value to be formatted
   * @param  {string|number|Decimal}  [defaultValue]     The default value to be formatted
   *
   * @returns {string}   Return a string of the monetary formatted representation of the given numeric value.
   *                     This includes a localized currency symbol and precision.
   */
  formatMoney(amount, defaultValue, symbol = true) {
    let formattedMoney = amount;

    try {
      formattedMoney = new Decimal(amount);

      // NaN is accepted by Decimal but we don't want to accept it here
      if (formattedMoney.isNaN()) {
        if (!_.isUndefined(defaultValue)) {
          formattedMoney = new Decimal(defaultValue);
        } else {
          formattedMoney = new Decimal(0);
        }
      }
    } catch (error) {
      if (!_.isUndefined(defaultValue)) {
        formattedMoney = new Decimal(defaultValue);
      } else {
        formattedMoney = new Decimal(0);
      }
    }

    formattedMoney =
      `${symbol ? Localize.getCurrencySymbol() : ''}` +
      `${formattedMoney.toFixed(Localize.getCurrencyPrecision())}`;

    return formattedMoney;
  },

  /**
   * Parses a monetary string into a decimal value.  If the value specified cannot be converted then
   * either the given default value or the original value will be returned.
   *
   * @param  {string} monetaryString  String value to be converted into a float.
   * @param  {Decimal} [defaultValue]    optional default Decimal value to return if the given value fails to convert
   * @returns {Decimal}       Returns the value as a Decimal number if the value can be converted.  If
   *                          the value cannot be converted then defaultValue is returned (if defined) otherwise
   *                          the original value is returned.
   */
  parseMoney(monetaryString, defaultValue) {
    // strip out the currency symbol if it exists
    let parsedMoney = monetaryString;
    if (_.isString(parsedMoney)) {
      parsedMoney = parsedMoney.replace(Localize.getCurrencySymbol(), '');
    }

    return Format.parseDecimal(parsedMoney, defaultValue);
  },

  /**
   * Parses a string into a formatted date string of the format YYYY-MM-DD.
   *
   * @param dateAsString {string}    A string representing a date.  It can be any format
   *                          that the Date JS API supports.
   * @param defaultValue {string}      The default value to return if the value cannot be
   *                          converted to a date.
   * @returns {*}
   */
  parseDate(dateAsString, defaultValue) {
    let parsedDate = dateAsString;
    let date;

    if (_.isString(dateAsString)) {
      date = new Date(dateAsString);
    }

    if (_.isDate(date) && !isNaN(date.getMonth())) {
      // TODO: Date format should be localized
      let month = `00${date.getMonth() + 1}`.substr(-2);
      let day = `00${date.getDate()}`.substr(-2);
      parsedDate = `${date.getFullYear()}-${month}-${day}`;
    } else if (!_.isUndefined(defaultValue)) {
      parsedDate = defaultValue;
    }

    return parsedDate;
  },
};

export default Format;
